<script lang="ts" setup>
import { defineAsyncComponent } from 'vue'
import dayjs from 'dayjs'
import { useSwiper } from '~/composables/common/use-swiper'
import LoadingSpin from '~common/loading-spin.vue'
import HotMatchEmpty from '~/components/common/hot-match/hot-match-empty.vue'
import { useSports } from '~/composables/sports/use-sports'
import { useHotmatches } from '~/composables/home/use-hotmatches'
import { DATE_TIME_FORMAT } from '~/constants'

const BaseImage = defineAsyncComponent(async () => await import('~/components/common/base-image.vue'))

const { modules } = useSwiper()
const { hotmatches, isLoadingHotmatch } = useHotmatches()
const { openKSportGame, playASportGame } = useSports()

const dateTimeConverted = (time: string, format: string): string => {
  const date = dayjs.utc(time.trim(), DATE_TIME_FORMAT.FULL_DATE_FORMAT_SCHEDULE).local()
  return date.format(format).replace('Invalid Date', '--/--/--')
}
defineProps({
  slidesPerView: {
    type: Number,
    default: 2.7
  },
  spaceBetween: {
    type: Number,
    default: 13
  },
  slidesOffsetBefore: {
    type: Number,
    default: 22
  },
  slidesOffsetAfter: {
    type: Number,
    default: 8
  }
})
</script>
<template>
  <div class="match-schedules">
    <ClientOnly>
      <LoadingSpin v-if="isLoadingHotmatch" />
      <div v-else class="schedules">
        <div v-if="hotmatches.length > 0">
          <div class="schedules__title">
            <div
              v-if="hotmatches?.length > 3"
              class="match-swiper-btns w-100 d-flex justify-content-end align-items-center"
            >
              <span class="d-flex gap-2">
                <div class="hotmatch__prev">
                  <span class="chevron-left"></span>
                </div>
                <div class="hotmatch__next">
                  <span class="chevron-right"></span>
                </div>
              </span>
            </div>
          </div>
          <div class="schedules__content">
            <Swiper
              v-if="hotmatches?.length"
              :modules="modules"
              :slidesPerView="slidesPerView"
              :navigation="{
                nextEl: '.hotmatch__next',
                prevEl: '.hotmatch__prev'
              }"
              :spaceBetween="spaceBetween"
              :slidesOffsetBefore="slidesOffsetBefore"
              :slidesOffsetAfter="slidesOffsetAfter"
            >
              <SwiperSlide v-for="(hotmatch, index) in hotmatches" :key="`hot-match+${index}`">
                <div class="match">
                  <h6>
                    <span class="time">
                      {{ dateTimeConverted(hotmatch?.text_time, DATE_TIME_FORMAT.HOUR_MINUTE_DEFAULT) }}
                    </span>
                    <span class="date">
                      <p class="date">{{ dateTimeConverted(hotmatch?.text_time, DATE_TIME_FORMAT.DAY_MONTH) }}</p>
                    </span>
                    <span class="dot">•</span>
                    <span class="text">{{ hotmatch?.league_name?.toUpperCase() }}</span>
                  </h6>
                  <div class="match-content">
                    <div class="team-logos">
                      <div class="home-team">
                        <BaseImage
                          :src="hotmatch?.teams[0].flag_thumbnail"
                          :alt="hotmatch?.teams[0].name"
                          imageDefault="/assets/images/common/no-item.png"
                        />
                      </div>
                      <div class="away-team">
                        <BaseImage
                          :src="hotmatch?.teams[1].flag_thumbnail"
                          :alt="hotmatch?.teams[1].name"
                          imageDefault="/assets/images/common/no-item.png"
                        />
                      </div>
                    </div>
                    <div class="match-team">
                      <p class="line-limit-1">{{ hotmatch?.teams[0].name }}</p>
                      <p class="line-limit-1">{{ hotmatch?.teams[1].name }}</p>
                    </div>
                    <div class="sport-types">
                      <button class="k-sport" @click.prevent="openKSportGame(hotmatch)">
                        <img src="/assets/images/pages/lobby-games/k-sport-logo.webp" width="16" alt="K-Sport" />
                        Cược
                      </button>
                      <button class="a-sport" @click.prevent="playASportGame(hotmatch)">
                        <img src="/assets/images/pages/lobby-games/a-sport-logo.svg" alt="K-Sport" />
                        Cược
                      </button>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>
        </div>
        <HotMatchEmpty v-else />
      </div>
    </ClientOnly>
  </div>
</template>
<style scoped lang="scss">
@import '~scssAsset/desktop/pages/home/match-schedule.scss';
</style>
